import type { ChangelogActionsData } from '../model/interfaces'
import type { ApiResponse } from '@shared/api'

import { getAxiosErrorMessage } from '@shared/api'
import { pick } from '@shared/lib/collections'
import { MAX_PER_PAGE, toPaginationValue, useAdminRoutes } from '@shared/lib/routes'
import { useToast } from '@shared/ui/toast'

import { useChangelogStore } from '../model/store'

export const useChangelogApi = async (): Promise<ChangelogActionsData | null> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()
  const route = useRoute()
  const store = useChangelogStore()

  const source = store.getListSource

  const url = useAdminRoutes(source?.route ?? 'admin.changelog.list', {
    ...source?.params,
    ...pick({
      query: store.getFilterSearch,
      from: store.getFilterFrom,
      to: store.getFilterTo,
      per_page: toPaginationValue(route.query?.per_page, store.getPerPage, MAX_PER_PAGE),
      cursor: store.getCursor,
    }),
  })

  store.setLoading(true)

  try {
    if (!url) {
      toast.error('Unexpected error. Invalid request URL.')
      return null
    }

    const { data } = await $axios.get<ApiResponse>(url)

    if (!data.success) {
      toast.error(data.message)
      return null
    }

    const payload: ChangelogActionsData | null = data.data || null
    store.setActions(payload?.actions ?? [])
    store.setMeta(payload?.meta ?? null)

    return payload
  } catch (err: unknown) {
    toast.error(getAxiosErrorMessage(err))
    return null
  } finally {
    store.setLoading(false)
  }
}
